import React, { FC, useCallback, useContext, useState } from 'react'
import { Link } from 'react-router-dom'
import classNames from 'classnames'
import { useFormik } from 'formik'
import { AxiosError } from 'axios'
import PageWrapper from '../../../layout/PageWrapper/PageWrapper'
import Page from '../../../layout/Page/Page'
import Card, { CardBody } from '../../../components/bootstrap/Card'
import FormGroup from '../../../components/bootstrap/forms/FormGroup'
import Input from '../../../components/bootstrap/forms/Input'
import Button from '../../../components/bootstrap/Button'
import Logo from '../../../components/Logo'
import useDarkMode from '../../../hooks/useDarkMode'
import AuthContext from '../../../contexts/authContext'
import Spinner from '../../../components/bootstrap/Spinner'
import authService from '../../../services/auth'
import { setAsideStatus } from '../../../utils/localStorage'

interface ILoginHeaderProps {
	isNewUser?: boolean
}
const LoginHeader: FC<ILoginHeaderProps> = ({ isNewUser }) => {
	if (isNewUser) {
		return (
			<>
				<div className="text-center h1 fw-bold mt-5">Create Account,</div>
				<div className="text-center h4 text-muted mb-5">Sign up to get started!</div>
			</>
		)
	}
	return (
		<>
			<div className="text-center h1 fw-bold mt-5">Καλωσήρθες,</div>
			<div className="text-center h4 text-muted mb-5">κάνε είσοδο για να συνεχίσεις!</div>
		</>
	)
}

interface ILoginProps {
	isSignUp?: boolean
}
const Login: FC<ILoginProps> = ({ isSignUp }) => {
	const { setUser, setUserData } = useContext(AuthContext)

	const { darkModeStatus } = useDarkMode()

	const [signInPassword, setSignInPassword] = useState<boolean>(false)
	const [singUpStatus] = useState<boolean>(!!isSignUp)
	const [showPassword, setShowPassword] = useState(false)

	const handleOnClick = useCallback(() => (window.location.href = '/'), [])

	const formik = useFormik({
		enableReinitialize: true,
		initialValues: {
			loginUsername: '',
			loginPassword: '',
		},
		validate: (values) => {
			const errors: { loginUsername?: string; loginPassword?: string } = {}

			if (!values.loginUsername) {
				errors.loginUsername = 'Απαραίτητο'
			}

			if (!values.loginPassword) {
				errors.loginPassword = 'Απαραίτητο'
			}

			return errors
		},
		validateOnChange: false,
		onSubmit: async (values) => {
			const credentials = {
				identifier: values.loginUsername,
				password: values.loginPassword,
			}

			try {
				const result = await authService.login(credentials)
				if (setUser && setUserData) {
					setUser(result.jwt)
					setUserData(result.user)
					handleOnClick()
					setAsideStatus(true)
				}
			} catch (error: unknown) {
				if (error instanceof AxiosError) {
					if (error.code === 'ERR_NETWORK') {
						formik.setFieldError('loginPassword', 'Παρουσιάστηκε σφάλμα σύνδεσης. Παρακαλώ επικοινωνήστε με τον διαχειριστή.')
					} else if (error.response?.status === 400 || error.response?.status === 401) {
						formik.setFieldError('loginPassword', 'Ο συνδυασμός email/όνομα χρήστη και κωδικού πρόσβασης δεν βρέθηκε.')
					} else {
						formik.setFieldError('loginPassword', 'Παρουσιάστηκε σφάλμα. Παρακαλώ δοκιμάστε ξανά.')
					}
				} else {
					formik.setFieldError('loginPassword', 'Παρουσιάστηκε σφάλμα. Παρακαλώ δοκιμάστε ξανά.')
				}
			}
		},
	})

	const [isLoading, setIsLoading] = useState<boolean>(false)

	const handleContinue = () => {
		setIsLoading(true)
		setTimeout(() => {
			setSignInPassword(true)
			setIsLoading(false)
		}, 1000)
	}

	// useEffect(() => {
	// 	localStorage.removeItem('jwtToken')
	// }, [])

	return (
		<PageWrapper isProtected={false} title={singUpStatus ? 'Sign Up' : 'Login'} className={classNames({ 'bg-dark': !singUpStatus, 'bg-light': singUpStatus })}>
			<Page className="p-0">
				<div className="row h-100 align-items-center justify-content-center">
					<div className="col-xl-4 col-lg-6 col-md-8 shadow-3d-container">
						<Card className="shadow-3d-dark" data-tour="login-page">
							<CardBody>
								<div className="text-center my-5">
									<Link
										to="/"
										className={classNames('text-decoration-none  fw-bold display-2', {
											'text-dark': !darkModeStatus,
											'text-light': darkModeStatus,
										})}
										aria-label="Facit"
									>
										<Logo width={200} />
									</Link>
								</div>
								<div
									className={classNames('rounded-3', {
										'bg-l10-dark': !darkModeStatus,
										'bg-dark': darkModeStatus,
									})}
								></div>

								<LoginHeader isNewUser={singUpStatus} />

								<form className="row g-4">
									{singUpStatus ? (
										<>
											{/* <div className="col-12">
												<FormGroup id="signup-email" isFloating label="Your email">
													<Input type="email" autoComplete="email" />
												</FormGroup>
											</div>
											<div className="col-12">
												<FormGroup id="signup-name" isFloating label="Your name">
													<Input autoComplete="given-name" />
												</FormGroup>
											</div>
											<div className="col-12">
												<FormGroup id="signup-surname" isFloating label="Your surname">
													<Input autoComplete="family-name" />
												</FormGroup>
											</div>
											<div className="col-12">
												<FormGroup id="signup-password" isFloating label="Password">
													<Input type="password" autoComplete="password" />
												</FormGroup>
											</div>
											<div className="col-12">
												<Button color="info" className="w-100 py-3" onClick={handleOnClick}>
													Sign Up
												</Button>
											</div> */}
										</>
									) : (
										<>
											<div className="col-12">
												<FormGroup
													id="loginUsername"
													isFloating
													label="Το email σου ή όνομα χρήστη"
													className={classNames({
														'd-none': signInPassword,
													})}
												>
													<Input
														autoComplete="username"
														value={formik.values.loginUsername}
														isTouched={formik.touched.loginUsername}
														invalidFeedback={formik.errors.loginUsername}
														isValid={formik.isValid}
														onChange={formik.handleChange}
														onBlur={formik.handleBlur}
														onFocus={() => {
															formik.setErrors({})
														}}
													/>
												</FormGroup>
												{signInPassword && <div className="text-center h4 mb-3 fw-bold">Γεια σου, {formik.values.loginUsername}.</div>}
												<div style={{ position: 'relative' }}>
													<FormGroup
														id="loginPassword"
														isFloating
														label="Κωδικός Πρόσβασης"
														className={classNames({
															'd-none': !signInPassword,
														})}
													>
														<Input
															type={!showPassword ? 'password' : 'text'}
															autoComplete="current-password"
															value={formik.values.loginPassword}
															isTouched={formik.touched.loginPassword}
															invalidFeedback={formik.errors.loginPassword}
															validFeedback="Looks good!"
															isValid={formik.isValid}
															onChange={formik.handleChange}
															onBlur={formik.handleBlur}
														/>
													</FormGroup>
													{signInPassword && (
														<span
															onClick={() => {
																setShowPassword((prevState) => !prevState)
															}}
															style={{ position: 'absolute', right: '10px', top: '14px', cursor: 'pointer' }}
														>
															{showPassword ? (
																<svg style={{ width: '20px' }} viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
																	<g id="SVGRepo_bgCarrier" stroke-width="0"></g>
																	<g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
																	<g id="SVGRepo_iconCarrier">
																		<path
																			clip-rule="evenodd"
																			d="M22.6928 1.55018C22.3102 1.32626 21.8209 1.45915 21.6 1.84698L19.1533 6.14375C17.4864 5.36351 15.7609 4.96457 14.0142 4.96457C9.32104 4.96457 4.781 7.84644 1.11993 13.2641L1.10541 13.2854L1.09271 13.3038C0.970762 13.4784 0.967649 13.6837 1.0921 13.8563C3.79364 17.8691 6.97705 20.4972 10.3484 21.6018L8.39935 25.0222C8.1784 25.4101 8.30951 25.906 8.69214 26.1299L9.03857 26.3326C9.4212 26.5565 9.91046 26.4237 10.1314 26.0358L23.332 2.86058C23.553 2.47275 23.4219 1.97684 23.0392 1.75291L22.6928 1.55018ZM18.092 8.00705C16.7353 7.40974 15.3654 7.1186 14.0142 7.1186C10.6042 7.1186 7.07416 8.97311 3.93908 12.9239C3.63812 13.3032 3.63812 13.8561 3.93908 14.2354C6.28912 17.197 8.86102 18.9811 11.438 19.689L12.7855 17.3232C11.2462 16.8322 9.97333 15.4627 9.97333 13.5818C9.97333 11.2026 11.7969 9.27368 14.046 9.27368C15.0842 9.27368 16.0317 9.68468 16.7511 10.3612L18.092 8.00705ZM15.639 12.3137C15.2926 11.7767 14.7231 11.4277 14.046 11.4277C12.9205 11.4277 12 12.3906 12 13.5802C12 14.3664 12.8432 15.2851 13.9024 15.3624L15.639 12.3137Z"
																			fill="#000000"
																			fill-rule="evenodd"
																		></path>
																		<path
																			d="M14.6873 22.1761C19.1311 21.9148 23.4056 19.0687 26.8864 13.931C26.9593 13.8234 27 13.7121 27 13.5797C27 13.4535 26.965 13.3481 26.8956 13.2455C25.5579 11.2677 24.1025 9.62885 22.5652 8.34557L21.506 10.2052C22.3887 10.9653 23.2531 11.87 24.0894 12.9239C24.3904 13.3032 24.3904 13.8561 24.0894 14.2354C21.5676 17.4135 18.7903 19.2357 16.0254 19.827L14.6873 22.1761Z"
																			fill="#000000"
																		></path>
																	</g>
																</svg>
															) : (
																<svg style={{ width: '20px' }} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
																	<g id="SVGRepo_bgCarrier" stroke-width="0"></g>
																	<g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
																	<g id="SVGRepo_iconCarrier">
																		{' '}
																		<path
																			d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12"
																			stroke="#000000"
																			stroke-width="2"
																			stroke-linecap="round"
																			stroke-linejoin="round"
																		></path>{' '}
																		<path
																			d="M1 12C1 12 5 20 12 20C19 20 23 12 23 12"
																			stroke="#000000"
																			stroke-width="2"
																			stroke-linecap="round"
																			stroke-linejoin="round"
																		></path>{' '}
																		<circle cx="12" cy="12" r="3" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></circle>{' '}
																	</g>
																</svg>
															)}
														</span>
													)}
												</div>
											</div>
											<div className="col-12">
												{!signInPassword ? (
													<Button color="warning" className="w-100 py-3" isDisable={!formik.values.loginUsername} onClick={handleContinue}>
														{isLoading && <Spinner isSmall inButton isGrow />}
														Συνέχεια
													</Button>
												) : (
													<Button color="warning" className="w-100 py-3" onClick={formik.handleSubmit}>
														Είσοδος
													</Button>
												)}
											</div>
										</>
									)}
								</form>
							</CardBody>
						</Card>
						<div className="text-center">
							<a
								href="/"
								className={classNames('text-decoration-none me-3', {
									'link-light': singUpStatus,
									'link-dark': !singUpStatus,
								})}
							>
								Privacy policy
							</a>
							<a
								href="/"
								className={classNames('link-light text-decoration-none', {
									'link-light': singUpStatus,
									'link-dark': !singUpStatus,
								})}
							>
								Terms of use
							</a>
						</div>
					</div>
				</div>
			</Page>
		</PageWrapper>
	)
}

export default Login
